import React from 'react';
import { Box } from '@mui/material';
import WbSunny from '@mui/icons-material/WbSunny';
import NightsStay from '@mui/icons-material/NightsStay';
import Cloud from '@mui/icons-material/Cloud';
import Foggy from '@mui/icons-material/BlurOn';
import Grain from '@mui/icons-material/Grain';
import Umbrella from '@mui/icons-material/Umbrella';
import AcUnit from '@mui/icons-material/AcUnit';
import Thunderstorm from '@mui/icons-material/Thunderstorm';
import HelpOutline from '@mui/icons-material/HelpOutline';
import type { MeteoIconKey } from '../../services/meteoApi';
import type { SvgIconProps } from '@mui/material/SvgIcon';

const CLOUD_WHITE = '#ECEFF1';
const SUN_YELLOW = '#FFD54F';
const RAIN_BLUE = '#29B6F6';
const SNOW_WHITE = '#E1F5FE';

/** Color “principal” (para estados no compuestos). */
export function getMeteoIconColor(
  icon?: MeteoIconKey | string | null,
  isDay = true
): string {
  switch (icon) {
    case 'clear':
      return isDay ? SUN_YELLOW : '#90CAF9';
    case 'mainly_clear':
    case 'partly_cloudy':
    case 'overcast':
      return CLOUD_WHITE;
    case 'fog':
      return '#B0BEC5';
    case 'drizzle':
      return '#4FC3F7';
    case 'rain':
      return RAIN_BLUE;
    case 'showers':
      return '#039BE5';
    case 'snow':
    case 'snow_showers':
      return SNOW_WHITE;
    case 'thunderstorm':
      return '#CE93D8';
    default:
      return '#9E9E9E';
  }
}

function readFontSize(sx: unknown, fallback = 18): number {
  if (!sx || typeof sx !== 'object') return fallback;
  const raw = (sx as { fontSize?: number | string }).fontSize;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const n = parseFloat(raw);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

/**
 * Nube blanca + sol asomando.
 * sunScale / cloudScale: fracción del tamaño del contenedor.
 */
function SunBehindCloud({
  size,
  sunScale,
  cloudScale = 1,
  isDay = true,
}: {
  size: number;
  sunScale: number;
  cloudScale?: number;
  isDay?: boolean;
}) {
  const cloudSize = Math.max(10, Math.round(size * cloudScale));
  const sunSize = Math.max(8, Math.round(size * sunScale));
  const SunOrMoon = isDay ? WbSunny : NightsStay;
  const sunColor = isDay ? SUN_YELLOW : '#90CAF9';
  const sunTop = Math.round(-size * 0.2);
  const sunRight = Math.round(-size * 0.08);
  const cloudLeft = Math.round((size - cloudSize) * 0.15);
  const cloudBottom = 0;

  return (
    <Box
      component="span"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        width: size,
        height: size,
        flexShrink: 0,
        verticalAlign: 'middle',
        overflow: 'visible',
      }}
    >
      <SunOrMoon
        sx={{
          position: 'absolute',
          fontSize: sunSize,
          color: sunColor,
          top: sunTop,
          right: sunRight,
          zIndex: 0,
        }}
      />
      <Cloud
        sx={{
          position: 'absolute',
          fontSize: cloudSize,
          color: CLOUD_WHITE,
          left: cloudLeft,
          bottom: cloudBottom,
          zIndex: 1,
        }}
      />
    </Box>
  );
}

/** Lluvia: nubecita arriba + rayas cortas diagonales debajo. */
function RainWithSmallCloud({ size }: { size: number }) {
  const cloudSize = Math.max(10, Math.round(size * 0.72));
  const streaks = [
    { x: 0.18, y: 0.58, h: 0.32 },
    { x: 0.38, y: 0.62, h: 0.36 },
    { x: 0.58, y: 0.56, h: 0.34 },
    { x: 0.76, y: 0.64, h: 0.3 },
  ];
  return (
    <Box
      component="span"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        width: size,
        height: size,
        flexShrink: 0,
        verticalAlign: 'middle',
        overflow: 'visible',
      }}
    >
      <Cloud
        sx={{
          position: 'absolute',
          fontSize: cloudSize,
          color: CLOUD_WHITE,
          left: '50%',
          top: Math.round(size * 0.02),
          transform: 'translateX(-50%)',
          zIndex: 1,
        }}
      />
      {streaks.map((s, i) => (
        <Box
          key={i}
          component="span"
          sx={{
            position: 'absolute',
            left: `${s.x * 100}%`,
            top: `${s.y * 100}%`,
            width: Math.max(1.5, size * 0.07),
            height: Math.max(5, size * s.h),
            borderRadius: 1,
            backgroundColor: RAIN_BLUE,
            transform: 'rotate(22deg)',
            transformOrigin: 'top center',
            opacity: 0.95 - i * 0.06,
            zIndex: 0,
          }}
        />
      ))}
    </Box>
  );
}

/** Chubascos de nieve: copos chicos dispersos (estilo llovizna), sin nube. */
function ScatteredSnowflakes({ size }: { size: number }) {
  const flakes = [
    { x: 0.08, y: 0.12, s: 0.38 },
    { x: 0.42, y: 0.02, s: 0.48 },
    { x: 0.68, y: 0.28, s: 0.34 },
    { x: 0.22, y: 0.52, s: 0.42 },
    { x: 0.58, y: 0.58, s: 0.36 },
  ];
  return (
    <Box
      component="span"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        width: size,
        height: size,
        flexShrink: 0,
        verticalAlign: 'middle',
        overflow: 'visible',
      }}
    >
      {flakes.map((f, i) => (
        <AcUnit
          key={i}
          sx={{
            position: 'absolute',
            left: `${f.x * 100}%`,
            top: `${f.y * 100}%`,
            fontSize: Math.max(6, Math.round(size * f.s)),
            color: SNOW_WHITE,
            opacity: 0.75 + (i % 3) * 0.08,
          }}
        />
      ))}
    </Box>
  );
}

/** Entradas para leyenda / preview en Settings. */
export const METEO_ICON_PREVIEW: Array<{
  icon: MeteoIconKey;
  labelKey: string;
  isDay?: boolean;
}> = [
  { icon: 'clear', labelKey: 'clear', isDay: true },
  { icon: 'clear', labelKey: 'clear', isDay: false },
  { icon: 'mainly_clear', labelKey: 'mainlyClear', isDay: true },
  { icon: 'partly_cloudy', labelKey: 'partlyCloudy', isDay: true },
  { icon: 'overcast', labelKey: 'overcast' },
  { icon: 'fog', labelKey: 'fog' },
  { icon: 'drizzle', labelKey: 'drizzle' },
  { icon: 'rain', labelKey: 'rain' },
  { icon: 'showers', labelKey: 'showers' },
  { icon: 'snow', labelKey: 'snow' },
  { icon: 'snow_showers', labelKey: 'snowShowers' },
  { icon: 'thunderstorm', labelKey: 'thunderstorm' },
];

export function MeteoConditionIcon({
  icon,
  isDay = true,
  sx,
  ...rest
}: {
  icon?: MeteoIconKey | string | null;
  isDay?: boolean;
} & SvgIconProps) {
  const size = readFontSize(sx, 18);
  const color = getMeteoIconColor(icon, isDay);
  const common = {
    sx: { fontSize: size, color, ...((sx as object) || {}) },
    ...rest,
  };

  switch (icon) {
    case 'clear':
      return isDay ? <WbSunny {...common} /> : <NightsStay {...common} />;
    case 'mainly_clear':
      return (
        <SunBehindCloud size={size} sunScale={0.95} cloudScale={0.72} isDay={isDay} />
      );
    case 'partly_cloudy':
      return (
        <SunBehindCloud size={size} sunScale={0.52} cloudScale={1} isDay={isDay} />
      );
    case 'overcast':
      return <Cloud {...common} sx={{ ...common.sx, color: CLOUD_WHITE }} />;
    case 'fog':
      return <Foggy {...common} />;
    case 'drizzle':
      return <Grain {...common} />;
    case 'rain':
      return <RainWithSmallCloud size={size} />;
    case 'showers':
      return <Umbrella {...common} />;
    case 'snow':
      return <AcUnit {...common} />;
    case 'snow_showers':
      return <ScatteredSnowflakes size={size} />;
    case 'thunderstorm':
      return <Thunderstorm {...common} />;
    default:
      return <HelpOutline {...common} />;
  }
}
